import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Environment, getEnvironment } from '@/utils/env';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import ContainerTypeSelector from '../../components/ContainerType/ContainerTypeSelector';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import { LayoutStoreInstance } from '../../LayoutStore';
import {
  CREATE_ZOHO_QUOTE_FOR_DEAL,
  CreateZohoQuoteForDealData,
  CreateZohoQuoteForDealVars,
  GET_ZOHO_DEAL_QUOTE_META,
  GetZohoDealQuoteMetaData,
  GetZohoDealQuoteMetaVars,
  QuoteContainerType,
} from './dealQuoteOperations';

type PriceRow = {
  boxAmount: string;
  rentPrice: string; // major units
  cleaningPrice: string;
  lossPrice: string;
  clearingPrice: string;
};

const NO_TAX = 'none';

// Zoho CRM Deal (Potentials) deep-link bases, mirroring ZohoEventButton.
const ZOHO_DEAL_URL_PROD =
  'https://one.zoho.eu/zohoone/vytalglobal/home/cxapp/crm/vytalglobal/tab/Potentials/';
const ZOHO_DEAL_URL_DEV =
  'https://crmsandbox.zoho.eu/crm/vytalcrmsandbox/tab/Potentials/';

const buildZohoDealUrl = (dealId: string): string =>
  `${
    getEnvironment() === Environment.Production
      ? ZOHO_DEAL_URL_PROD
      : ZOHO_DEAL_URL_DEV
  }${dealId}`;

const centsToInput = (cents: number): string => (cents / 100).toFixed(2);
const inputToCents = (value: string): number =>
  Math.round(parseFloat(value || '0') * 100) || 0;

const emptyRowFromDefaults = (ct: QuoteContainerType): PriceRow => ({
  boxAmount: '0',
  rentPrice: centsToInput(ct.defaultRentPrice),
  cleaningPrice: centsToInput(ct.defaultCleaningPrice),
  lossPrice: centsToInput(ct.defaultLossPrice),
  clearingPrice: centsToInput(ct.defaultClearingPrice),
});

/**
 * Hidden admin page (URL-only) to build a Zoho Books quote (estimate) for a Zoho
 * Deal. The Deal id comes from the URL. Users pick event_portfolio container
 * types, edit default prices + box amounts, choose which price types
 * (Rent/Cleaning/Loss/Clearing) to include, the language and the tax, and submit
 * to create the quote in the correct Zoho Books instance.
 *
 * Uses the shadcn/ui design system (matching the /edit-container-types screen).
 */
export const DealQuotePage: React.FC = () => {
  const { zohoDealId } = useParams<{ zohoDealId: string }>();

  const { data, loading, error } = useQuery<
    GetZohoDealQuoteMetaData,
    GetZohoDealQuoteMetaVars
  >(GET_ZOHO_DEAL_QUOTE_META, {
    variables: { dealId: zohoDealId },
    fetchPolicy: 'cache-and-network',
  });

  const meta = data?.getZohoDealQuoteMeta;

  const [language, setLanguage] = useState<string>('DE');
  const [taxId, setTaxId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<number, PriceRow>>({});

  // Item-type toggles: all on by default except Clearing.
  const [includeRent, setIncludeRent] = useState(true);
  const [includeCleaning, setIncludeCleaning] = useState(true);
  const [includeLoss, setIncludeLoss] = useState(true);
  const [includeClearing, setIncludeClearing] = useState(false);

  const [createdQuote, setCreatedQuote] = useState<{
    url: string;
    number: string | null;
  } | null>(null);

  // Seed language + tax defaults from Zoho once the meta arrives.
  useEffect(() => {
    if (meta) {
      setLanguage(meta.defaultLanguage || 'DE');
      setTaxId(meta.suggestedTaxId || '');
    }
  }, [meta?.defaultLanguage, meta?.suggestedTaxId]);

  const containerTypeById = useMemo(() => {
    const map = new Map<number, QuoteContainerType>();
    meta?.containerTypes.forEach((ct) => map.set(ct.id, ct));
    return map;
  }, [meta?.containerTypes]);

  // Container types for the selector (already filtered to event_portfolio server-side).
  const selectorData: ContainerType[] = useMemo(
    () =>
      (meta?.containerTypes || []).map((ct) => ({
        id: ct.id,
        name: ct.name,
        class: 'event_portfolio',
        group: ct.group,
        iconUrl: ct.iconUrl,
        imageUrl: ct.imageUrl,
        namesByLanguage: null,
        resolvedName: ct.name,
        sizeHelper: null,
        hexCode: ct.hexCode,
        packingUnit: ct.packingUnit,
        archived: false,
      })),
    [meta?.containerTypes],
  );

  // Keep the editable rows in sync with the selection: add defaults for newly
  // selected types, keep existing edits, drop deselected ones.
  useEffect(() => {
    setRows((prev) => {
      const next: Record<number, PriceRow> = {};
      for (const idStr of selectedIds) {
        const id = parseInt(idStr, 10);
        const ct = containerTypeById.get(id);
        if (!ct) continue;
        next[id] = prev[id] || emptyRowFromDefaults(ct);
      }
      return next;
    });
  }, [selectedIds, containerTypeById]);

  const [createQuote, { loading: submitting }] = useMutation<
    CreateZohoQuoteForDealData,
    CreateZohoQuoteForDealVars
  >(CREATE_ZOHO_QUOTE_FOR_DEAL);

  useDocumentTitle('PageTitles:createZohoQuote');

  const updateRow = (id: number, field: keyof PriceRow, value: string) => {
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const removeContainer = (id: number) => {
    setSelectedIds((prev) =>
      prev.filter((idStr) => parseInt(idStr, 10) !== id),
    );
  };

  // Reorder a row; selectedIds order is the table order AND the order the
  // container-type sections appear in the created quote.
  const moveContainer = (id: number, direction: 'up' | 'down') => {
    setSelectedIds((prev) => {
      const index = prev.findIndex((idStr) => parseInt(idStr, 10) === id);
      if (index < 0) return prev;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      LayoutStoreInstance.setError(
        'Please select at least one container type.',
      );
      return;
    }
    if (!includeRent && !includeCleaning && !includeLoss && !includeClearing) {
      LayoutStoreInstance.setError('Please select at least one item type.');
      return;
    }

    const lineItems = selectedIds
      .map((idStr) => {
        const id = parseInt(idStr, 10);
        const row = rows[id];
        if (!row) return null;
        return {
          containerTypeId: id,
          boxAmount: parseInt(row.boxAmount || '0', 10) || 0,
          rentPrice: inputToCents(row.rentPrice),
          cleaningPrice: inputToCents(row.cleaningPrice),
          lossPrice: inputToCents(row.lossPrice),
          clearingPrice: inputToCents(row.clearingPrice),
        };
      })
      .filter((li): li is NonNullable<typeof li> => li !== null);

    try {
      const result = await createQuote({
        variables: {
          input: {
            dealId: zohoDealId,
            language,
            taxId: taxId || null,
            includeRent,
            includeCleaning,
            includeLoss,
            includeClearing,
            lineItems,
          },
        },
      });
      const created = result.data?.createZohoQuoteForDeal;
      if (created) {
        setCreatedQuote({
          url: created.estimateUrl,
          number: created.estimateNumber,
        });
        LayoutStoreInstance.setSuccess(
          `Quote ${created.estimateNumber || ''} created.`,
        );
      }
    } catch (e: any) {
      LayoutStoreInstance.setError(e?.message || 'Failed to create the quote.');
    }
  };

  if (loading && !meta) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:p-8 tw:text-sm tw:text-muted-foreground">
        <Spinner /> Loading deal…
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:max-w-[900px] tw:p-4">
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Failed to load deal</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!meta) {
    return null;
  }

  const itemTypeToggles: Array<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
  }> = [
    { label: 'Rent', checked: includeRent, onChange: setIncludeRent },
    {
      label: 'Cleaning',
      checked: includeCleaning,
      onChange: setIncludeCleaning,
    },
    { label: 'Loss', checked: includeLoss, onChange: setIncludeLoss },
    {
      label: 'Clearing',
      checked: includeClearing,
      onChange: setIncludeClearing,
    },
  ];

  return (
    <div className="tw:flex tw:max-w-[1200px] tw:flex-col tw:gap-6 tw:p-4 tw:pb-16">
      {/* Page header */}
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <h1 className="tw:text-3xl tw:font-semibold tw:tracking-tight">
            Create Zoho quote
          </h1>
          <p className="tw:text-sm tw:text-muted-foreground">
            {meta.dealName || 'Untitled deal'} · {meta.dealId}
          </p>
        </div>
        <div className="tw:flex tw:flex-none tw:gap-2">
          <Badge variant="secondary">{meta.entity}</Badge>
          <Badge variant="outline">{meta.currency}</Badge>
        </div>
      </div>

      {/* Deal details */}
      <Card>
        <CardHeader>
          <CardTitle>Deal</CardTitle>
          <CardDescription>Fetched from Zoho CRM</CardDescription>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-5">
          {/* Row 1: read-only deal info */}
          <div className="tw:grid tw:gap-5 tw:sm:grid-cols-2">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:text-xs tw:font-medium tw:text-muted-foreground">
                Deal
              </span>
              <a
                href={buildZohoDealUrl(meta.dealId)}
                target="_blank"
                rel="noreferrer"
                className="tw:inline-flex tw:w-fit tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-primary tw:underline"
              >
                {meta.dealName || '—'}
                <ExternalLink size={13} />
              </a>
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:text-xs tw:font-medium tw:text-muted-foreground">
                Main contractor
              </span>
              <span className="tw:text-sm">
                {meta.mainContractor?.name || '— (none set)'}
                {meta.mainContractor?.billingCountryCode
                  ? ` · ${meta.mainContractor.billingCountryCode}`
                  : ''}
              </span>
            </div>
          </div>

          {/* Row 2: quote settings */}
          <div className="tw:grid tw:gap-5 tw:sm:grid-cols-2">
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label
                htmlFor="language"
                className="tw:text-xs tw:font-medium tw:text-muted-foreground"
              >
                Language
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language" className="tw:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DE">German</SelectItem>
                  <SelectItem value="EN">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label
                htmlFor="tax"
                className="tw:text-xs tw:font-medium tw:text-muted-foreground"
              >
                Tax
              </Label>
              <Select
                value={taxId || NO_TAX}
                onValueChange={(v) => setTaxId(v === NO_TAX ? '' : v)}
              >
                <SelectTrigger id="tax" className="tw:w-full">
                  <SelectValue placeholder="Select tax" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TAX}>No tax</SelectItem>
                  {meta.availableTaxes.map((tax) => (
                    <SelectItem key={tax.taxId} value={tax.taxId}>
                      {tax.taxName}
                      {tax.taxPercentage != null
                        ? ` (${tax.taxPercentage}%)`
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {!meta.mainContractor && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>No main contractor</AlertTitle>
          <AlertDescription>
            This deal has no Main contractor account set. A quote cannot be
            created without a customer.
          </AlertDescription>
        </Alert>
      )}

      {/* Item types */}
      <Card>
        <CardHeader>
          <CardTitle>Item types</CardTitle>
          <CardDescription>
            Which price types to add per container type
          </CardDescription>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-wrap tw:gap-6">
          {itemTypeToggles.map((toggle) => (
            <label
              key={toggle.label}
              className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:text-sm"
            >
              <Checkbox
                checked={toggle.checked}
                onCheckedChange={(checked) => toggle.onChange(checked === true)}
              />
              {toggle.label}
            </label>
          ))}
        </CardContent>
      </Card>

      {/* Container types + pricing */}
      <Card>
        <CardHeader>
          <CardTitle>Container types</CardTitle>
          <CardDescription>
            Event portfolio — rows appear in the quote in this order
          </CardDescription>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-4">
          <ContainerTypeSelector
            data={selectorData}
            value={selectedIds}
            searchable
            clearable
            defaultFilterValue="event_portfolio"
            onChange={(value) =>
              setSelectedIds(Array.isArray(value) ? value : [])
            }
          />

          {selectedIds.length === 0 ? (
            <div className="tw:rounded-md tw:border tw:border-dashed tw:px-4 tw:py-8 tw:text-center tw:text-sm tw:text-muted-foreground">
              No container types selected yet. Use the selector above to add
              some.
            </div>
          ) : (
            <div className="tw:overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Container type</TableHead>
                    <TableHead>Box amount</TableHead>
                    {includeRent && (
                      <TableHead>Rent ({meta.currency})</TableHead>
                    )}
                    {includeCleaning && (
                      <TableHead>Cleaning ({meta.currency})</TableHead>
                    )}
                    {includeLoss && (
                      <TableHead>Loss ({meta.currency})</TableHead>
                    )}
                    {includeClearing && (
                      <TableHead>Clearing ({meta.currency})</TableHead>
                    )}
                    <TableHead className="tw:w-[50px]">Order</TableHead>
                    <TableHead> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedIds.map((idStr, index) => {
                    const id = parseInt(idStr, 10);
                    const ct = containerTypeById.get(id);
                    const row = rows[id];
                    if (!ct || !row) return null;
                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <div className="tw:flex tw:items-center tw:gap-2">
                            {ct.hexCode && (
                              <span
                                className="tw:inline-block tw:size-3 tw:flex-none tw:rounded-sm tw:border"
                                style={{ backgroundColor: `#${ct.hexCode}` }}
                              />
                            )}
                            <div className="tw:flex tw:flex-col">
                              <span className="tw:font-medium">{ct.name}</span>
                              <span className="tw:text-xs tw:text-muted-foreground">
                                Packing unit: {ct.packingUnit}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            className="tw:w-24"
                            value={row.boxAmount}
                            onChange={(e) =>
                              updateRow(id, 'boxAmount', e.target.value)
                            }
                          />
                        </TableCell>
                        {includeRent && (
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="tw:w-28"
                              value={row.rentPrice}
                              onChange={(e) =>
                                updateRow(id, 'rentPrice', e.target.value)
                              }
                            />
                          </TableCell>
                        )}
                        {includeCleaning && (
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="tw:w-28"
                              value={row.cleaningPrice}
                              onChange={(e) =>
                                updateRow(id, 'cleaningPrice', e.target.value)
                              }
                            />
                          </TableCell>
                        )}
                        {includeLoss && (
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="tw:w-28"
                              value={row.lossPrice}
                              onChange={(e) =>
                                updateRow(id, 'lossPrice', e.target.value)
                              }
                            />
                          </TableCell>
                        )}
                        {includeClearing && (
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              className="tw:w-28"
                              value={row.clearingPrice}
                              onChange={(e) =>
                                updateRow(id, 'clearingPrice', e.target.value)
                              }
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="tw:flex tw:items-center tw:justify-end tw:gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Move ${ct.name} up`}
                              disabled={index === 0}
                              onClick={() => moveContainer(id, 'up')}
                            >
                              <ChevronUp size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Move ${ct.name} down`}
                              disabled={index === selectedIds.length - 1}
                              onClick={() => moveContainer(id, 'down')}
                            >
                              <ChevronDown size={16} />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${ct.name}`}
                            onClick={() => removeContainer(id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Created quote confirmation */}
      {createdQuote && (
        <Alert>
          <CheckCircle2 />
          <AlertTitle>Quote {createdQuote.number || ''} created</AlertTitle>
          <AlertDescription>
            <a
              href={createdQuote.url}
              target="_blank"
              rel="noreferrer"
              className="tw:inline-flex tw:items-center tw:gap-1 tw:font-medium tw:underline"
            >
              Open quote in Zoho Books
              <ExternalLink size={14} />
            </a>
          </AlertDescription>
        </Alert>
      )}

      {/* Action */}
      <div className="tw:flex tw:items-center tw:gap-3">
        <Button
          disabled={submitting || !meta.mainContractor}
          onClick={handleSubmit}
        >
          {submitting ? 'Creating…' : 'Create quote'}
        </Button>
      </div>
    </div>
  );
};

export default DealQuotePage;
